import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "../contexts/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  EnvelopeSimple, Upload, Plus, X, CaretLeft, Link as LinkIcon, Copy, Check, Users,
  ArrowsClockwise, Warning
} from "@phosphor-icons/react";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { toast } from "sonner";

export default function InviteCandidates() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [assessment, setAssessment] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [aRes, iRes] = await Promise.all([
        axios.get(`${API}/assessments/${id}`, { withCredentials: true }),
        axios.get(`${API}/assessments/${id}/invitations`, { withCredentials: true }),
      ]);
      setAssessment(aRes.data);
      setInvitations(iRes.data);
    } catch (err) {
      toast.error("Failed to load assessment");
    }
  };

  const addEmail = () => {
    const emails = emailInput.split(/[,;\n\s]+/).filter(e => e.includes("@"));
    const newEmails = [...new Set([...emailList, ...emails.map(e => e.trim().toLowerCase())])];
    setEmailList(newEmails);
    setEmailInput("");
  };

  const removeEmail = (email) => {
    setEmailList(prev => prev.filter(e => e !== email));
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/csv-emails`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newEmails = [...new Set([...emailList, ...res.data.emails])];
      setEmailList(newEmails);
      toast.success(`Found ${res.data.emails.length} emails`);
    } catch {
      toast.error("CSV upload failed");
    }
  };

  const handleSendInvites = async () => {
    if (emailList.length === 0) {
      toast.error("Add at least one email");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assessments/${id}/invite`, { emails: emailList }, { withCredentials: true });
      toast.success(res.data.message);
      setEmailList([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invites");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (token) => {
    const link = `${window.location.origin}/take/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast.success("Link copied!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const statusColor = (s) => {
    if (s === "completed") return "bg-[#10B981]/10 text-[#10B981]";
    if (s === "started") return "bg-[#F59E0B]/10 text-[#F59E0B]";
    return "bg-[#52525B]/10 text-[#52525B]";
  };

  const isExpired = (inv) => {
    if (inv.is_expired) return true;
    if (inv.status === "completed") return false;
    const exp = inv.expires_at;
    if (!exp) return false;
    return new Date(exp) < new Date();
  };

  const handleResend = async (invitationId) => {
    setResendingId(invitationId);
    try {
      const res = await axios.post(`${API}/invitations/${invitationId}/resend`, {}, { withCredentials: true });
      if (res.data.new_link_generated) {
        toast.success("Expired link replaced with a new one");
      } else {
        toast.success("Invitation resent with existing link");
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    } finally {
      setResendingId(null);
    }
  };

  if (!assessment) {
    return (
      <div className="min-h-screen pt-14 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF3366] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-14 bg-white" data-testid="invite-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-xs text-[#52525B] hover:text-[#0A0A0A] mb-4" data-testid="back-link">
          <CaretLeft size={14} /> Dashboard
        </button>
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">
              Invite Candidates
            </h1>
            <p className="text-sm text-[#52525B] mt-1">{assessment.title}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate(`/assessments/${id}/settings`)}
              variant="outline"
              className="rounded-none border-black/20 text-sm"
              data-testid="settings-link"
            >
              Settings
            </Button>
            <Button
              onClick={() => navigate(`/assessments/${id}/results`)}
              variant="outline"
              className="rounded-none border-black/20 text-sm"
              data-testid="view-results-link"
            >
              View Results
            </Button>
          </div>
        </div>

        <div className="border border-black/10 p-6 mb-6" data-testid="email-input-section">
          <h3 className="font-heading font-semibold text-sm text-[#0A0A0A] mb-4">Add Candidate Emails</h3>
          <div className="flex gap-2 mb-3">
            <Input
              value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              placeholder="Enter emails (comma separated)"
              className="rounded-none border-black/20 flex-1"
              onKeyDown={e => e.key === "Enter" && addEmail()}
              data-testid="email-input"
            />
            <Button onClick={addEmail} variant="outline" className="rounded-none border-black/20 px-4" data-testid="add-email-btn">
              <Plus size={16} />
            </Button>
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 px-3 text-xs font-medium text-[#52525B] border border-black/20 hover:bg-black/5 transition-colors"
              data-testid="upload-csv-btn"
            >
              <Upload size={14} /> CSV
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCSVUpload} />
          </div>

          {emailList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4" data-testid="email-tags">
              {emailList.map(email => (
                <span key={email} className="inline-flex items-center gap-1 px-2 py-1 bg-[#F8F9FA] border border-black/10 text-xs">
                  {email}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button onClick={() => removeEmail(email)} className="hover:text-[#EF4444]">
                        <X size={12} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Remove</TooltipContent>
                  </Tooltip>
                </span>
              ))}
            </div>
          )}

          <Button
            onClick={handleSendInvites}
            disabled={loading || emailList.length === 0}
            className="bg-[#FF3366] text-white hover:bg-[#E62E5C] rounded-none px-6 py-2.5 h-auto font-semibold text-sm w-full"
            data-testid="send-invites-btn"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <EnvelopeSimple size={16} /> Send {emailList.length} Invitation{emailList.length !== 1 ? 's' : ''}
              </span>
            )}
          </Button>
        </div>

        {invitations.length > 0 && (
          <div className="border border-black/10" data-testid="invitations-list">
            <div className="px-5 py-3 border-b border-black/10 bg-[#F8F9FA] flex items-center justify-between">
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">
                Invited Candidates ({invitations.length})
              </span>
            </div>
            {invitations.map((inv) => {
              const expired = isExpired(inv);
              return (
                <div key={inv.invitation_id} className={`px-5 py-3 border-b border-black/5 hover:bg-[#F8F9FA] transition-colors flex items-center justify-between ${expired ? 'bg-[#EF4444]/[0.02]' : ''}`}
                  data-testid={`invitation-row-${inv.invitation_id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 border flex items-center justify-center ${expired ? 'bg-[#EF4444]/5 border-[#EF4444]/20' : 'bg-[#F8F9FA] border-black/10'}`}>
                      {expired ? <Warning size={14} className="text-[#EF4444]" /> : <EnvelopeSimple size={14} className="text-[#52525B]" />}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#0A0A0A]">{inv.candidate_email}</p>
                      <p className={`text-[10px] ${expired ? 'text-[#EF4444] font-semibold' : 'text-[#52525B]'}`}>
                        {expired ? 'Expired: ' : 'Expires: '}{new Date(inv.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {expired && inv.status !== "completed" && (
                      <span className="text-[9px] px-1.5 py-0.5 font-bold uppercase bg-[#EF4444]/10 text-[#EF4444]">
                        expired
                      </span>
                    )}
                    {!expired && (
                      <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${statusColor(inv.status)}`}>
                        {inv.status}
                      </span>
                    )}
                    {inv.result && (
                      <span className={`text-xs font-bold tabular-nums ${inv.result.score >= 70 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {inv.result.score}%
                      </span>
                    )}
                    {inv.status !== "completed" && (
                      <button
                        onClick={() => handleResend(inv.invitation_id)}
                        disabled={resendingId === inv.invitation_id}
                        className={`flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border transition-colors
                          ${expired
                            ? 'border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366] hover:text-white'
                            : 'border-black/10 text-[#52525B] hover:bg-black/5'}`}
                        data-testid={`resend-btn-${inv.invitation_id}`}
                        title={expired ? "Generate new link & resend" : "Resend existing link"}
                      >
                        {resendingId === inv.invitation_id ? (
                          <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ArrowsClockwise size={12} />
                        )}
                        {expired ? "New Link" : "Resend"}
                      </button>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => copyLink(inv.token)}
                          className="p-1.5 hover:bg-black/5 transition-colors"
                          data-testid={`copy-link-${inv.invitation_id}`}
                        >
                          {copiedToken === inv.token ? <Check size={14} className="text-[#10B981]" /> : <Copy size={14} className="text-[#52525B]" />}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="rounded-none bg-[#0A0A0A] text-white border-none text-[11px] px-2 py-1">Copy assessment link</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
